import React from 'react';
import { Navigate } from 'react-router-dom';
import Loader from '@/app/routes/Loader';
import InlineLayout from '@/shared/layout/inlineLayout';
import { Snackbar } from '@/shared/components/Snackbar';
import { Tooltip } from '@/shared/components/Tooltip';
import { useUserRole } from '@/shared/hooks/useUserRole';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import {
  ACKNOWLEDGE_SR_LABELS,
  ASSIGN_TECHNICIAN_LABELS,
  CANCEL_SERVICE_REQUEST_LABELS,
  RESCHEDULE_SR_LABELS,
  TECHNICIAN_UPDATE_SR_STATUS_LABELS,
  UPDATE_SR_STATUS_LABELS,
  VIEW_SERVICE_REQUEST_LABELS as L,
} from '../../constants/labels';
import {
  SR_NAV_IDS,
  SR_NAV_ITEMS,
  useViewServiceRequest,
} from '../../hooks/useViewServiceRequest';
import { useAcknowledgeServiceRequest } from '../../hooks/useAcknowledgeServiceRequest';
import { useAssignTechnician } from '../../hooks/useAssignTechnician';
import { useCancelServiceRequest } from '../../hooks/useCancelServiceRequest';
import { useUpdateServiceRequestStatus } from '../../hooks/useUpdateServiceRequestStatus';
import { useUpdateTaskStatus } from '../../hooks/useUpdateTaskStatus';
import { useReschedule } from '../../hooks/useReschedule';
import { isReschedulableNow } from '../../utils/reschedulePolicy';
import { isCancellableNow } from '../../utils/cancellationPolicy';
import type { RescheduleServiceRequestResponseData } from '../../types/rescheduleServiceRequestType';
import AcknowledgeServiceRequestModal from './AcknowledgeServiceRequestModal';
import CancelServiceRequestModal from './CancelServiceRequestModal';
import SRSummaryTab from './SRSummaryTab';
import SRFacilityTab from './SRFacilityTab';
import SREquipmentTab from './SREquipmentTab';
import SRDetailsAttachmentsTab from './SRDetailsAttachmentsTab';
import SRHistoryTab from './SRHistoryTab';
import SRAdminNotesTab from './SRAdminNotesTab';
import SRTechnicianTab from './SRTechnicianTab';
import ViewServiceRequestErrorScreen from './ViewServiceRequestErrorScreen';
import UpdateStatusModal from './UpdateStatusModal';
import TechnicianUpdateStatusModal from './TechnicianUpdateStatusModal';
import AssignTechnicianModal from './AssignTechnicianModal';
import RescheduleModal from './RescheduleModal';
import { SRStatusBadge } from './viewServiceRequestUI';
import { formatFacilityLocation } from './viewServiceRequestHelpers';

const buildRescheduleSnackbar = (
  outcome: RescheduleServiceRequestResponseData['notification_sent'] | undefined
): string => {
  if (!outcome) {
    return RESCHEDULE_SR_LABELS.SUCCESS_MESSAGE;
  }
  if (!outcome.client && !outcome.technician) {
    return `${RESCHEDULE_SR_LABELS.SUCCESS_MESSAGE} ${RESCHEDULE_SR_LABELS.SUCCESS_BOTH_EMAILS_FAILED}`;
  }
  if (!outcome.client) {
    return `${RESCHEDULE_SR_LABELS.SUCCESS_MESSAGE} ${RESCHEDULE_SR_LABELS.SUCCESS_CLIENT_EMAIL_FAILED}`;
  }
  if (!outcome.technician) {
    return `${RESCHEDULE_SR_LABELS.SUCCESS_MESSAGE} ${RESCHEDULE_SR_LABELS.SUCCESS_TECH_EMAIL_FAILED}`;
  }
  return RESCHEDULE_SR_LABELS.SUCCESS_MESSAGE;
};

/** Props for {@link ViewServiceRequest}. */
interface ViewServiceRequestProps {
  /** Internal UUID of the service request (`service_requests.id`). Passed from the route param. */
  serviceRequestId: string;
}

/**
 * Full-detail view of a single service request.
 *
 * @remarks
 * **Role behaviour:**
 * - `CLIENT` — sees the cancel button (hidden once the request is `cancelled` or `closed`).
 *   Opens {@link CancelServiceRequestModal} managed by {@link useCancelServiceRequest}.
 *   When a visit is scheduled (`scheduled_start_at` is set):
 *   - ≥ 24 h away → info banner + enabled button
 *   - < 24 h away → warning banner + disabled button
 * - `ADMIN` / `SUPER_ADMIN` — sees the change-status button (opens {@link UpdateStatusModal})
 *   and, when the status is `in_review` or `rejected`, the assign button (opens {@link AssignTechnicianModal}).
 *
 * All three action flows call `refetch()` on success so the page reflects the
 * latest state without a full navigation reload.
 *
 * Renders {@link ViewServiceRequestErrorScreen} on a non-404 load failure and
 * redirects to `/404` when the request is not found.
 *
 * @param props - See {@link ViewServiceRequestProps}.
 */
const ViewServiceRequest: React.FC<ViewServiceRequestProps> = ({
  serviceRequestId,
}) => {
  const { detail, isLoading, showErrorScreen, notFound, refetch } =
    useViewServiceRequest(serviceRequestId);
  const { role, isClient, isTechnician } = useUserRole();
  const { privileges } = useUserPrivileges();
  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const showTechnicianActions =
    isTechnician &&
    detail?.status === 'assigned' &&
    privileges.canAcceptRejectTask;
  // True once the scheduled visit start has passed. When no start is set the
  // gate doesn't apply (treated as passed). Drives the `accepted` case below.
  const scheduledStartAtMs = detail?.scheduled_start_at
    ? new Date(detail.scheduled_start_at).getTime()
    : null;
  const scheduledStartPassed =
    scheduledStartAtMs === null ||
    Number.isNaN(scheduledStartAtMs) ||
    Date.now() >= scheduledStartAtMs;
  // Technician update-status is actionable on the statuses they can advance
  // from (accepted → in_progress, in_progress → unfulfilled). A technician
  // can only start an `accepted` task once its scheduled start time has
  // passed; `in_progress` is never time-gated.
  const showTechnicianUpdateStatus =
    isTechnician &&
    (detail?.status === 'in_progress' ||
      (detail?.status === 'accepted' && scheduledStartPassed));

  const isWithin24Hours = React.useMemo(() => {
    if (!detail?.scheduled_start_at) {
      return false;
    }
    const visitTime = new Date(detail.scheduled_start_at).getTime();
    return visitTime - Date.now() < 24 * 60 * 60 * 1000;
  }, [detail?.scheduled_start_at]);

  const [statusSuccessMessage, setStatusSuccessMessage] = React.useState<
    string | null
  >(null);
  const [showAssignSuccessSnackbar, setShowAssignSuccessSnackbar] =
    React.useState(false);
  const [rescheduleSnackbarMessage, setRescheduleSnackbarMessage] =
    React.useState<string | null>(null);
  const [
    showTaskStatusPermissionDeniedSnackbar,
    setShowTaskStatusPermissionDeniedSnackbar,
  ] = React.useState(false);
  // Set once the backend rejects a technician status update with 403 — the
  // privilege was revoked mid-session, so we hide the button straight away
  // (the mount-time `useUserPrivileges` snapshot can't pick this up live).
  const [taskStatusPermissionRevoked, setTaskStatusPermissionRevoked] =
    React.useState(false);
  const [acknowledgeSuccessMessage, setAcknowledgeSuccessMessage] =
    React.useState<string | null>(null);
  const [
    showAcknowledgePermissionDeniedSnackbar,
    setShowAcknowledgePermissionDeniedSnackbar,
  ] = React.useState(false);
  const [
    showAcknowledgeWindowExpiredSnackbar,
    setShowAcknowledgeWindowExpiredSnackbar,
  ] = React.useState(false);

  const acknowledge = useAcknowledgeServiceRequest({
    serviceRequestId: detail?.id,
    onSuccess: (status) => {
      setAcknowledgeSuccessMessage(
        status === 'accepted'
          ? ACKNOWLEDGE_SR_LABELS.ACCEPT_SUCCESS
          : ACKNOWLEDGE_SR_LABELS.REJECT_SUCCESS
      );
      void refetch();
    },
    onPermissionDenied: () => {
      setShowAcknowledgePermissionDeniedSnackbar(true);
      void refetch();
    },
    onWindowExpired: () => {
      setShowAcknowledgeWindowExpiredSnackbar(true);
      void refetch();
    },
  });

  const updateStatus = useUpdateServiceRequestStatus({
    serviceRequestId: detail?.id,
    currentStatus: detail?.status,
    scheduledStartAt: detail?.scheduled_start_at,
    onSuccess: (submittedStatus) => {
      setStatusSuccessMessage(
        submittedStatus === 'cancelled'
          ? CANCEL_SERVICE_REQUEST_LABELS.SUCCESS_MESSAGE
          : UPDATE_SR_STATUS_LABELS.SUCCESS_MESSAGE
      );
      void refetch();
    },
  });

  const updateTaskStatus = useUpdateTaskStatus({
    serviceRequestId: detail?.id,
    currentStatus: detail?.status,
    onSuccess: () => {
      setStatusSuccessMessage(
        TECHNICIAN_UPDATE_SR_STATUS_LABELS.SUCCESS_MESSAGE
      );
      void refetch();
    },
    onPermissionDenied: () => {
      setShowTaskStatusPermissionDeniedSnackbar(true);
      setTaskStatusPermissionRevoked(true);
    },
  });

  const cancelEligibility = isCancellableNow(
    detail?.status,
    detail?.scheduled_start_at
  );
  const showCancelWithin24hBanner =
    !cancelEligibility.canCancel && cancelEligibility.reason === 'WITHIN_24H';

  const assign = useAssignTechnician({
    serviceRequestId: detail?.id,
    onSuccess: () => {
      setShowAssignSuccessSnackbar(true);
      void refetch();
    },
  });

  const [showCancelSuccessSnackbar, setShowCancelSuccessSnackbar] =
    React.useState(false);
  const [
    showCancelPermissionDeniedSnackbar,
    setShowCancelPermissionDeniedSnackbar,
  ] = React.useState(false);

  const cancelRequest = useCancelServiceRequest({
    serviceRequestId: detail?.id,
    onSuccess: () => {
      setShowCancelSuccessSnackbar(true);
      void refetch();
    },
    onPermissionDenied: () => {
      setShowCancelPermissionDeniedSnackbar(true);
    },
  });

  const reschedule = useReschedule({
    serviceRequestId: detail?.id,
    currentStatus: detail?.status,
    prefill: {
      scheduledStartAt: detail?.scheduled_start_at,
      scheduledEndAt: detail?.scheduled_end_at,
      technicianName: detail?.assigned_to?.name ?? null,
    },
    onSuccess: (data) => {
      setRescheduleSnackbarMessage(
        buildRescheduleSnackbar(data?.notification_sent)
      );
      void refetch();
    },
    onStaleStatus: () => {
      void refetch();
    },
  });

  const canAssign = detail?.status === 'in_review';

  const rescheduleEligibility = isReschedulableNow(
    detail?.status,
    detail?.scheduled_start_at
  );
  // Spec: visible whenever status ∈ RESCHEDULABLE_FROM (even when blocked by
  // the 24h rule — show disabled with tooltip).
  const showRescheduleCta =
    rescheduleEligibility.canReschedule ||
    rescheduleEligibility.reason === 'WITHIN_24H';
  const rescheduleDisabled = !rescheduleEligibility.canReschedule;

  const handleCancelRequest = () => {
    cancelRequest.open();
  };

  const handleChangeStatus = () => {
    updateStatus.open();
  };

  const handleUpdateTaskStatus = () => {
    updateTaskStatus.open();
  };

  const handleAssign = () => {
    assign.open();
  };

  const handleReschedule = () => {
    reschedule.open();
  };

  // Technician accept/reject — UI only for now. Wiring to the status-update
  const handleAccept = () => acknowledge.openAccept();
  const handleReject = () => acknowledge.openReject();

  const rescheduleButton = (
    <button
      type="button"
      onClick={rescheduleDisabled ? undefined : handleReschedule}
      disabled={rescheduleDisabled}
      className={`h-10 bg-[#2A4455] px-4 py-2.5 rounded-lg flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] transition-colors ${
        rescheduleDisabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-[#1f3340] cursor-pointer'
      }`}
      data-testid="view-sr-reschedule-button"
      aria-disabled={rescheduleDisabled}
    >
      <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
        {L.BUTTON_RESCHEDULE}
      </span>
    </button>
  );

  const cancelWithin24hBanner = showCancelWithin24hBanner ? (
    <div
      className="flex items-start gap-2 w-full rounded-lg"
      style={{
        backgroundColor: '#FFF7E0',
        padding: '14px 20px',
      }}
      role="status"
      data-testid="view-sr-cancel-within-24h-banner"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="shrink-0"
      >
        <path
          d="M12 3 1.5 21h21L12 3Z"
          fill="#FF9800"
          stroke="#FF9800"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="M12 10v5"
          stroke="#FFFFFF"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <circle cx="12" cy="17.5" r="1" fill="#FFFFFF" />
      </svg>
      <span
        style={{
          fontFamily: 'Roboto',
          fontSize: 14,
          lineHeight: '20px',
          fontWeight: 400,
          color: '#FF9800',
        }}
      >
        {L.BANNER_CANNOT_CANCEL_WITHIN_24H}
      </span>
    </div>
  ) : null;

  const header = (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex items-center justify-between w-full gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <h2
            className="font-medium text-neutral-10 min-w-0 break-all"
            style={{ fontSize: '18px', lineHeight: '28px' }}
            data-testid="view-sr-business-id"
          >
            {detail?.service_request_id ?? '—'}
          </h2>
          {detail && <SRStatusBadge status={detail.status} />}
        </div>
        {isClient &&
          detail?.status !== 'cancelled' &&
          detail?.status !== 'closed' &&
          (() => {
            const noPrivilege = !privileges.canCancelServiceRequest;
            const isDisabled = noPrivilege || isWithin24Hours;
            const tooltipContent = noPrivilege
              ? CANCEL_SERVICE_REQUEST_LABELS.NO_PRIVILEGE_TOOLTIP
              : L.BANNER_CANCEL_DISABLED;
            const cancelButton = (
              <button
                type="button"
                onClick={isDisabled ? undefined : handleCancelRequest}
                disabled={isDisabled}
                className={`h-10 bg-[#2A4455] px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] shrink-0 ${
                  isDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-[#1f3340] cursor-pointer'
                }`}
                data-testid="view-sr-cancel-button"
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                  {L.BUTTON_CANCEL_REQUEST}
                </span>
              </button>
            );
            return isDisabled ? (
              <Tooltip
                content={tooltipContent}
                forceShow
                multiline
                cursor="not-allowed"
                side="bottom"
                align="right"
                contentMaxWidth={320}
                triggerTabIndex={-1}
                triggerWrapContent
              >
                {cancelButton}
              </Tooltip>
            ) : (
              cancelButton
            );
          })()}
        {showTechnicianActions && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={
                acknowledge.isAccepting || acknowledge.isRejecting
                  ? undefined
                  : handleReject
              }
              disabled={acknowledge.isAccepting || acknowledge.isRejecting}
              className={`h-10 shrink-0 bg-[#E74141] px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E74141] ${
                acknowledge.isAccepting || acknowledge.isRejecting
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-[#d03a3a] cursor-pointer'
              }`}
              data-testid="view-sr-reject-button"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px] whitespace-nowrap">
                {acknowledge.isRejecting
                  ? ACKNOWLEDGE_SR_LABELS.REJECT_BUTTON_SUBMITTING
                  : L.BUTTON_REJECT}
              </span>
            </button>
            <button
              type="button"
              onClick={
                acknowledge.isAccepting || acknowledge.isRejecting
                  ? undefined
                  : handleAccept
              }
              disabled={acknowledge.isAccepting || acknowledge.isRejecting}
              className={`h-10 shrink-0 bg-[#2DBA8D] px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2DBA8D] ${
                acknowledge.isAccepting || acknowledge.isRejecting
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-[#27a67d] cursor-pointer'
              }`}
              data-testid="view-sr-accept-button"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px] whitespace-nowrap">
                {acknowledge.isAccepting
                  ? ACKNOWLEDGE_SR_LABELS.ACCEPT_BUTTON_SUBMITTING
                  : L.BUTTON_ACCEPT}
              </span>
            </button>
          </div>
        )}
        {showTechnicianUpdateStatus &&
          privileges.canUpdateTaskStatus &&
          !taskStatusPermissionRevoked && (
            <button
              type="button"
              onClick={handleUpdateTaskStatus}
              className="h-10 bg-white border border-[#DCDDE0] px-4 py-2.5 rounded-lg hover:bg-[#F3F4F6] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] cursor-pointer shrink-0"
              data-testid="view-sr-technician-update-status-button"
            >
              <span className="text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px]">
                {L.BUTTON_CHANGE_STATUS}
              </span>
            </button>
          )}
        {isAdmin && (
          <div className="flex items-center gap-3 shrink-0">
            {detail?.status !== 'cancelled' && detail?.status !== 'closed' && (
              <button
                type="button"
                onClick={handleChangeStatus}
                className="h-10 bg-white border border-[#DCDDE0] px-4 py-2.5 rounded-lg hover:bg-[#F3F4F6] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] cursor-pointer"
                data-testid="view-sr-change-status-button"
              >
                <span className="text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px]">
                  {L.BUTTON_CHANGE_STATUS}
                </span>
              </button>
            )}
            {canAssign && (
              <button
                type="button"
                onClick={handleAssign}
                className="h-10 bg-[#2A4455] px-4 py-2.5 rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] cursor-pointer"
                data-testid="view-sr-assign-button"
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                  {L.BUTTON_ASSIGN}
                </span>
              </button>
            )}
            {showRescheduleCta &&
              (rescheduleDisabled ? (
                <Tooltip
                  content={L.BUTTON_RESCHEDULE_TOOLTIP_24H}
                  side="bottom"
                  align="right"
                  inlineTrigger
                >
                  {rescheduleButton}
                </Tooltip>
              ) : (
                rescheduleButton
              ))}
          </div>
        )}
      </div>
      {isAdmin && cancelWithin24hBanner}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  return (
    <React.Fragment key={serviceRequestId}>
      {showErrorScreen ? (
        <ViewServiceRequestErrorScreen />
      ) : (
        <InlineLayout
          title={L.PAGE_TITLE}
          navItems={[...SR_NAV_ITEMS]}
          contentByNavId={{
            [SR_NAV_IDS.Summary]: <SRSummaryTab detail={detail} />,
            [SR_NAV_IDS.Facility]: <SRFacilityTab detail={detail} />,
            [SR_NAV_IDS.Equipment]: <SREquipmentTab detail={detail} />,
            [SR_NAV_IDS.Details]: <SRDetailsAttachmentsTab detail={detail} />,
            [SR_NAV_IDS.Technician]: <SRTechnicianTab detail={detail} />,
            [SR_NAV_IDS.AdminNotes]: (
              <SRAdminNotesTab adminNotes={detail?.admin_notes ?? null} />
            ),
            [SR_NAV_IDS.History]: <SRHistoryTab detail={detail} />,
          }}
          header={header}
          searchParamKey="tab"
          defaultActiveId={SR_NAV_IDS.Summary}
          tabletNavVariant="fit"
          paymentBanner={
            isClient &&
            detail?.status !== 'completed' &&
            detail?.status !== 'cancelled' &&
            detail?.status !== 'closed' ? (
              isWithin24Hours ? (
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#FFF6E5]"
                  data-testid="cancel-sr-warning-banner"
                >
                  <img
                    src="/assets/alert-triangle (1).svg"
                    alt=""
                    aria-hidden="true"
                    className="w-5 h-5 shrink-0"
                  />
                  <p className="text-sm text-warning-40">
                    {L.BANNER_CANCEL_DISABLED}
                  </p>
                </div>
              ) : (
                <div
                  className="flex items-center gap-3 px-5 py-2 rounded-lg"
                  data-testid="cancel-sr-info-banner"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    className="shrink-0"
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="9"
                      stroke="#474A4C"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M10 9v5"
                      stroke="#474A4C"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <circle cx="10" cy="6.5" r="0.75" fill="#474A4C" />
                  </svg>
                  <p
                    className="text-sm text-[#474A4C]"
                    style={{ letterSpacing: '0.25px' }}
                  >
                    {L.BANNER_CANCEL_INFO}
                  </p>
                </div>
              )
            ) : null
          }
        />
      )}
      {detail && isAdmin && (
        <UpdateStatusModal
          open={updateStatus.isOpen}
          onClose={updateStatus.close}
          currentStatus={detail.status}
          allowedStatuses={updateStatus.allowedStatuses}
          step={updateStatus.step}
          onContinue={updateStatus.goToCancelReasonStep}
          onGoBackToForm={updateStatus.goBackToForm}
          newStatus={updateStatus.newStatus}
          onNewStatusChange={updateStatus.setNewStatus}
          newStatusError={updateStatus.newStatusError}
          adminNotes={updateStatus.adminNotes}
          onAdminNotesChange={updateStatus.setAdminNotes}
          adminNotesError={updateStatus.adminNotesError}
          notify={updateStatus.notify}
          onNotifyChange={updateStatus.setNotify}
          cancellationReason={updateStatus.cancellationReason}
          onCancellationReasonChange={updateStatus.setCancellationReason}
          cancellationReasonError={updateStatus.cancellationReasonError}
          customReason={updateStatus.customReason}
          onCustomReasonChange={updateStatus.setCustomReason}
          customReasonError={updateStatus.customReasonError}
          summary={{
            scheduledStartAt: detail.scheduled_start_at,
            scheduledEndAt: detail.scheduled_end_at,
            serviceTypeName: detail.service_type_name,
            technicianName: detail.assigned_to?.name ?? null,
            clientName: detail.client_name,
          }}
          isSubmitting={updateStatus.isSubmitting}
          submitError={updateStatus.submitError}
          onSubmit={updateStatus.submit}
          canSubmit={updateStatus.canSubmit}
          canContinue={updateStatus.canContinue}
        />
      )}
      {detail && showTechnicianUpdateStatus && (
        <TechnicianUpdateStatusModal
          open={updateTaskStatus.isOpen}
          onClose={updateTaskStatus.close}
          currentStatus={detail.status}
          allowedStatuses={updateTaskStatus.allowedStatuses}
          step={updateTaskStatus.step}
          onContinue={updateTaskStatus.goToDetailsStep}
          newStatus={updateTaskStatus.newStatus}
          onNewStatusChange={updateTaskStatus.setNewStatus}
          newStatusError={updateTaskStatus.newStatusError}
          notes={updateTaskStatus.notes}
          onNotesChange={updateTaskStatus.setNotes}
          notesError={updateTaskStatus.notesError}
          missedVisitReason={updateTaskStatus.missedVisitReason}
          onMissedVisitReasonChange={updateTaskStatus.setMissedVisitReason}
          missedVisitReasonError={updateTaskStatus.missedVisitReasonError}
          summary={{
            scheduledStartAt: detail.scheduled_start_at,
            scheduledEndAt: detail.scheduled_end_at,
            serviceTypeName: detail.service_type_name,
            clientName: detail.client_name,
            location: formatFacilityLocation([
              detail.facility_address_line1,
              detail.facility_address_line2,
              detail.facility_postal_code,
              detail.facility_city,
              detail.facility_state,
              detail.facility_country,
            ]),
          }}
          isSubmitting={updateTaskStatus.isSubmitting}
          submitError={updateTaskStatus.submitError}
          onSubmit={updateTaskStatus.submit}
          canSubmit={updateTaskStatus.canSubmit}
        />
      )}
      {detail && isClient && (
        <CancelServiceRequestModal
          open={cancelRequest.isOpen}
          onClose={cancelRequest.close}
          reason={cancelRequest.reason}
          onReasonChange={cancelRequest.setReason}
          reasonError={cancelRequest.reasonError}
          isSubmitting={cancelRequest.isSubmitting}
          submitError={cancelRequest.submitError}
          canSubmit={cancelRequest.canSubmit}
          onSubmit={cancelRequest.submit}
        />
      )}
      {detail && isAdmin && showRescheduleCta && (
        <RescheduleModal
          open={reschedule.isOpen}
          onClose={reschedule.close}
          serviceRequestId={detail.id}
          summary={{
            scheduledStartAt: detail.scheduled_start_at,
            scheduledEndAt: detail.scheduled_end_at,
            taskFormId: detail.task_form?.form_id ?? null,
            taskFormName: detail.task_form?.name ?? null,
            certificateId:
              detail.certificate_template?.cert_template_id ?? null,
            certificateName: detail.certificate_template?.name ?? null,
            serviceTypeName: detail.service_type_name,
            technicianName: detail.assigned_to?.name ?? null,
            clientName: detail.client_name,
          }}
          step={reschedule.step}
          onGoToConfirm={reschedule.goToConfirm}
          onGoBackToForm={reschedule.goBackToForm}
          previousSnapshot={reschedule.previousSnapshot}
          startDate={reschedule.startDate}
          endDate={reschedule.endDate}
          startTime={reschedule.startTime}
          endTime={reschedule.endTime}
          startDateIso={reschedule.startDateIso}
          endDateIso={reschedule.endDateIso}
          onStartDateChange={reschedule.setStartDate}
          onEndDateChange={reschedule.setEndDate}
          onStartTimeChange={reschedule.setStartTime}
          onEndTimeChange={reschedule.setEndTime}
          startDateError={reschedule.startDateError}
          endDateError={reschedule.endDateError}
          endTimeError={reschedule.endTimeError}
          windowComplete={reschedule.windowComplete}
          assignToRole={reschedule.assignToRole}
          onAssignToRoleChange={reschedule.setAssignToRole}
          assignToError={reschedule.assignToError}
          selectedTechnicianId={reschedule.selectedTechnicianId}
          selectedTechnicianCode={reschedule.selectedTechnicianCode}
          selectedTechnicianName={reschedule.selectedTechnicianName}
          onSelectTechnician={reschedule.setSelectedTechnician}
          technicianError={reschedule.technicianError}
          prefillTechnicianName={reschedule.prefillTechnicianName}
          notify={reschedule.notify}
          onNotifyChange={reschedule.setNotify}
          isSubmitting={reschedule.isSubmitting}
          submitError={reschedule.submitError}
          canSubmit={reschedule.canSubmit}
          onSubmit={reschedule.submit}
        />
      )}
      {detail && isAdmin && canAssign && (
        <AssignTechnicianModal
          open={assign.isOpen}
          onClose={assign.close}
          serviceRequestId={detail.id}
          startDate={assign.startDate}
          endDate={assign.endDate}
          startTime={assign.startTime}
          endTime={assign.endTime}
          startDateIso={assign.startDateIso}
          endDateIso={assign.endDateIso}
          onStartDateChange={assign.setStartDate}
          onEndDateChange={assign.setEndDate}
          onStartTimeChange={assign.setStartTime}
          onEndTimeChange={assign.setEndTime}
          startDateError={assign.startDateError}
          endDateError={assign.endDateError}
          endTimeError={assign.endTimeError}
          windowComplete={assign.windowComplete}
          selectedTaskFormId={assign.selectedTaskFormId}
          selectedTaskFormFormId={assign.selectedTaskFormFormId}
          selectedTaskFormName={assign.selectedTaskFormName}
          onSelectTaskForm={assign.setSelectedTaskForm}
          taskFormError={assign.taskFormError}
          linkedCertificateName={assign.linkedCertificateName}
          linkedCertificateId={assign.linkedCertificateId}
          noCertificateError={assign.noCertificateError}
          selectedTechnicianId={assign.selectedTechnicianId}
          selectedTechnicianCode={assign.selectedTechnicianCode}
          onSelectTechnician={assign.setSelectedTechnician}
          technicianError={assign.technicianError}
          isSubmitting={assign.isSubmitting}
          submitError={assign.submitError}
          canSubmit={assign.canSubmit}
          onSubmit={assign.submit}
        />
      )}
      <Snackbar
        open={!!statusSuccessMessage}
        message={statusSuccessMessage ?? ''}
        variant="success"
        onClose={() => setStatusSuccessMessage(null)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showTaskStatusPermissionDeniedSnackbar}
        message={TECHNICIAN_UPDATE_SR_STATUS_LABELS.PERMISSION_DENIED_SNACKBAR}
        variant="error"
        onClose={() => setShowTaskStatusPermissionDeniedSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showAssignSuccessSnackbar}
        message={ASSIGN_TECHNICIAN_LABELS.SUCCESS_MESSAGE}
        variant="success"
        onClose={() => setShowAssignSuccessSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showCancelSuccessSnackbar}
        message={CANCEL_SERVICE_REQUEST_LABELS.SUCCESS_MESSAGE}
        variant="success"
        onClose={() => setShowCancelSuccessSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showCancelPermissionDeniedSnackbar}
        message={CANCEL_SERVICE_REQUEST_LABELS.PERMISSION_DENIED_SNACKBAR}
        variant="error"
        onClose={() => setShowCancelPermissionDeniedSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={!!rescheduleSnackbarMessage}
        message={rescheduleSnackbarMessage ?? ''}
        variant="success"
        onClose={() => setRescheduleSnackbarMessage(null)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={!!acknowledgeSuccessMessage}
        message={acknowledgeSuccessMessage ?? ''}
        variant="success"
        onClose={() => setAcknowledgeSuccessMessage(null)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showAcknowledgePermissionDeniedSnackbar}
        message={ACKNOWLEDGE_SR_LABELS.PERMISSION_DENIED_SNACKBAR}
        variant="error"
        onClose={() => setShowAcknowledgePermissionDeniedSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showAcknowledgeWindowExpiredSnackbar}
        message={ACKNOWLEDGE_SR_LABELS.WINDOW_EXPIRED_SNACKBAR}
        variant="error"
        onClose={() => setShowAcknowledgeWindowExpiredSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      {detail && isTechnician && showTechnicianActions && (
        <>
          <AcknowledgeServiceRequestModal
            variant="accepted"
            open={acknowledge.isAcceptOpen}
            onClose={acknowledge.closeAccept}
            scheduledStartAt={detail.scheduled_start_at}
            scheduledEndAt={detail.scheduled_end_at}
            isSubmitting={acknowledge.isSubmitting}
            submitError={acknowledge.submitError}
            onSubmit={acknowledge.submitAccept}
          />
          <AcknowledgeServiceRequestModal
            variant="rejected"
            open={acknowledge.isRejectOpen}
            onClose={acknowledge.closeReject}
            scheduledStartAt={detail.scheduled_start_at}
            scheduledEndAt={detail.scheduled_end_at}
            reason={acknowledge.reason}
            onReasonChange={acknowledge.setReason}
            reasonError={acknowledge.reasonError}
            canSubmit={acknowledge.canSubmit}
            isSubmitting={acknowledge.isSubmitting}
            submitError={acknowledge.submitError}
            onSubmit={acknowledge.submitReject}
          />
        </>
      )}
    </React.Fragment>
  );
};

export default ViewServiceRequest;
